import { useState } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { User, Mail, Fuel, Droplets, MapPin, Calendar, MessageSquare, Send, Building2, Phone } from "lucide-react";
import { z } from "zod";

const Contact = () => {
  const { language } = useLanguage();
  const [consentChecked, setConsentChecked] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const content = {
    orders: language === 'sv' ? 'Beställningar' : 'Orders',
    orderFuel: language === 'sv' ? 'Beställningsförfrågan Drivmedel' : 'Order Fuel',
    description: language === 'sv'
      ? 'Vi kommer att bekräfta leveransen på mail eller på telefon och återkommer eventuellt om det är några frågor. I normalfallet så behöver vi beställningen 3 arbetsdagar före leveransen.'
      : 'We will confirm the delivery by email or telephone and will get back to you if there are any questions. Normally, we need the order 3 working days before delivery.',
    nameCompany: language === 'sv' ? 'Namn/Företag' : 'Name/Company',
    email: language === 'sv' ? 'E-postadress' : 'Email',
    product: language === 'sv' ? 'Önskad Produkt' : 'Desired Product',
    volume: language === 'sv' ? 'Önskad Volym (liter)' : 'Desired Volume (liters)',
    location: language === 'sv' ? 'Leveransplats' : 'Delivery Location',
    date: language === 'sv' ? 'Datum' : 'Date',
    additionalInfo: language === 'sv' ? 'Övrig information' : 'Additional information',
    sendMessage: language === 'sv' ? 'Skicka beställning' : 'Send order',
    namePlaceholder: language === 'sv' ? 'Ert namn eller företag' : 'Your name or company',
    emailPlaceholder: language === 'sv' ? 'Er e-postadress' : 'Your email',
    locationPlaceholder: language === 'sv' ? 'Ange leveransplats' : 'Enter delivery location',
    additionalInfoPlaceholder: language === 'sv' ? 'Övrig information som kan vara relevant för beställningen' : 'Any additional information relevant to your order',
    postalAddress: language === 'sv' ? 'Postadress' : 'Postal Address',
    visitingAddress: language === 'sv' ? 'Besöksadress' : 'Visiting Address',
    fuelOrders: language === 'sv' ? 'Beställning av drivmedel' : 'Fuel Orders',
    economyAdmin: language === 'sv' ? 'Ekonomi & Administration' : 'Economy & Administration',
    consentLabel: language === 'sv' 
      ? 'Jag godkänner att Petrobell behandlar mina personuppgifter enligt' 
      : 'I agree that Petrobell processes my personal data according to the',
    privacyPolicy: language === 'sv' ? 'integritetspolicyn' : 'privacy policy',
    consentRequired: language === 'sv' 
      ? 'Du måste godkänna behandlingen av personuppgifter för att skicka beställningen' 
      : 'You must consent to the processing of personal data to send the order'
  };

  const products = [
    "HVO100",
    language === 'sv' ? "Bensin 98" : "Petrol 98",
    language === 'sv' ? "Diesel utan RME" : "Diesel without RME",
    language === 'sv' ? "Diesel MK1 färgad" : "Diesel MK1 coloured",
    language === 'sv' ? "Gasoil E10 färgad" : "Gasoil E10 coloured",
    language === 'sv' ? "Gasoil E32 färgad" : "Gasoil E32 coloured"
  ];

  // Validation schema
  const orderSchema = z.object({
    name: z.string().trim().min(1, language === 'sv' ? 'Namn krävs' : 'Name is required').max(100),
    email: z.string().trim().email(language === 'sv' ? 'Ogiltig e-postadress' : 'Invalid email address').max(255),
    product: z.string().min(1, language === 'sv' ? 'Välj en produkt' : 'Select a product'),
    volume: z.string().min(1, language === 'sv' ? 'Ange volym' : 'Enter volume'),
    location: z.string().trim().min(1, language === 'sv' ? 'Ange leveransplats' : 'Enter delivery location').max(500),
    date: z.string().min(1, language === 'sv' ? 'Välj datum' : 'Select date'),
    additionalInfo: z.string().max(2000).optional()
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    if (!consentChecked) {
      setErrors({ consent: content.consentRequired });
      return;
    }

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      product: formData.get('product') as string,
      volume: formData.get('volume') as string,
      location: formData.get('location') as string,
      date: formData.get('date') as string,
      additionalInfo: formData.get('additionalInfo') as string
    };

    // Validate form data
    const result = orderSchema.safeParse(data);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }
    
    const message = `
${content.nameCompany}: ${data.name}
${content.product}: ${data.product}
${content.volume}: ${data.volume}
${content.location}: ${data.location}
${content.date}: ${data.date}
${content.additionalInfo}: ${data.additionalInfo || '-'}
    `;
    
    window.location.href = `mailto:leveranser@petrobell.se?subject=${encodeURIComponent((language === 'sv' ? 'Beställningsförfrågan från ' : 'Order request from ') + data.name)}&body=${encodeURIComponent(message)}`;
  };

  return (
    <section id="order-form" className="min-h-screen py-20 relative flex items-center bg-[url('/images/hav-bg.jpg')] bg-cover bg-center bg-fixed bg-no-repeat">
      <div className="absolute inset-0 bg-black/50 z-0 fixed"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-6 py-2 mb-6 text-sm font-semibold bg-white/20 backdrop-blur-sm rounded-full text-white border border-white/30">
              {content.orders}
            </span>
            <h2 className="text-2xl md:text-5xl font-bold text-white mb-6 break-words">
              {content.orderFuel}
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto text-base md:text-lg leading-relaxed px-2">
              {content.description}
            </p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16 bg-white/10 backdrop-blur-md p-8 md:p-10 rounded-2xl border border-white/20 shadow-2xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Name/Company */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white/90">
                  <User className="h-4 w-4" />
                  {content.nameCompany}
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  maxLength={100}
                  className="w-full px-4 py-4 rounded-xl border border-white/20 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all placeholder:text-white/40 hover:bg-white/10"
                  placeholder={content.namePlaceholder}
                />
                {errors.name && <p className="text-red-300 text-sm">{errors.name}</p>}
              </div>
              
              {/* Email */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white/90">
                  <Mail className="h-4 w-4" />
                  {content.email}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  maxLength={255}
                  className="w-full px-4 py-4 rounded-xl border border-white/20 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all placeholder:text-white/40 hover:bg-white/10"
                  placeholder={content.emailPlaceholder}
                />
                {errors.email && <p className="text-red-300 text-sm">{errors.email}</p>}
              </div>
              
              {/* Product */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white/90">
                  <Fuel className="h-4 w-4" />
                  {content.product}
                </label>
                <Select name="product" required>
                  <SelectTrigger className="w-full h-14 px-4 rounded-xl border-white/20 bg-white/5 text-white focus:ring-2 focus:ring-white/40 hover:bg-white/10 transition-all">
                    <SelectValue placeholder={language === 'sv' ? 'Välj produkt...' : 'Select product...'} />
                  </SelectTrigger>
                  <SelectContent className="bg-primary/95 backdrop-blur-md border-white/20 rounded-xl">
                    {products.map((product) => (
                      <SelectItem 
                        key={product} 
                        value={product} 
                        className="text-white hover:bg-white/10 focus:bg-white/10 rounded-lg cursor-pointer"
                      >
                        {product}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.product && <p className="text-red-300 text-sm">{errors.product}</p>}
              </div>
              
              {/* Volume */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white/90">
                  <Droplets className="h-4 w-4" />
                  {content.volume}
                </label>
                <input
                  type="number"
                  name="volume"
                  required
                  min="1"
                  max="1000000"
                  className="w-full px-4 py-4 rounded-xl border border-white/20 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all hover:bg-white/10"
                  placeholder="1000"
                />
                {errors.volume && <p className="text-red-300 text-sm">{errors.volume}</p>}
              </div>

              {/* Location */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white/90">
                  <MapPin className="h-4 w-4" />
                  {content.location}
                </label>
                <input
                  type="text"
                  name="location"
                  required
                  maxLength={500}
                  className="w-full px-4 py-4 rounded-xl border border-white/20 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all placeholder:text-white/40 hover:bg-white/10"
                  placeholder={content.locationPlaceholder}
                />
                {errors.location && <p className="text-red-300 text-sm">{errors.location}</p>}
              </div>
              
              {/* Date */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-white/90">
                  <Calendar className="h-4 w-4" />
                  {content.date}
                </label>
                <input
                  type="date"
                  name="date"
                  required
                  className="w-full px-4 py-4 rounded-xl border border-white/20 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all hover:bg-white/10 [&::-webkit-calendar-picker-indicator]:invert"
                />
                {errors.date && <p className="text-red-300 text-sm">{errors.date}</p>}
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-8 space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-white/90">
                <MessageSquare className="h-4 w-4" />
                {content.additionalInfo}
              </label>
              <Textarea
                name="additionalInfo"
                maxLength={2000}
                className="w-full px-4 py-4 rounded-xl border border-white/20 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all min-h-[120px] placeholder:text-white/40 hover:bg-white/10 resize-none"
                placeholder={content.additionalInfoPlaceholder}
              />
            </div>

            {/* GDPR Consent Checkbox */}
            <div className="mt-8 space-y-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consentChecked}
                  onCheckedChange={(checked) => {
                    setConsentChecked(checked as boolean);
                    if (checked) {
                      setErrors((prev) => ({ ...prev, consent: '' }));
                    }
                  }}
                  className="mt-1 border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-primary"
                />
                <label htmlFor="consent" className="text-sm text-white/80 leading-relaxed cursor-pointer">
                  {content.consentLabel}{' '}
                  <Link to="/privacy-policy" className="text-white underline hover:text-white/80 transition-colors">
                    {content.privacyPolicy}
                  </Link>
                  .
                </label>
              </div>
              {errors.consent && <p className="text-red-300 text-sm">{errors.consent}</p>}
            </div>

            <motion.div 
              className="mt-8"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <Button
                type="submit"
                className="w-full h-14 bg-white text-primary rounded-xl font-semibold text-lg transition-all hover:bg-white/90 hover:shadow-xl hover:shadow-white/20 flex items-center justify-center gap-3"
              >
                <Send className="h-5 w-5" />
                {content.sendMessage}
              </Button>
            </motion.div>
          </motion.form>
          
          {/* Contact Info Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/15 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/20 rounded-lg group-hover:bg-white/30 transition-all">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-white">{content.postalAddress}</h3>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                Petrobell AB<br />
                Ranhammarsvägen 4<br />
                168 67 Bromma
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/15 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/20 rounded-lg group-hover:bg-white/30 transition-all">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-white">{content.visitingAddress}</h3>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                Petrobell AB<br />
                Ranhammarsvägen 4<br />
                168 67 Bromma
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/15 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/20 rounded-lg group-hover:bg-white/30 transition-all">
                  <Fuel className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-white">{content.fuelOrders}</h3>
              </div>
              <div className="space-y-2">
                <a href="mailto:leveranser@petrobell.se" className="block text-white/80 text-sm hover:text-white transition-colors">
                  leveranser@petrobell.se
                </a>
                <a href="tel:+46705376100" className="flex items-center gap-2 text-white/80 text-sm hover:text-white transition-colors">
                  <Phone className="h-3 w-3" />
                  070-537 61 00
                </a>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/15 transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/20 rounded-lg group-hover:bg-white/30 transition-all">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-white">{content.economyAdmin}</h3>
              </div>
              <div className="space-y-2">
                <a href="mailto:Fredrik@petrobell.se" className="block text-white/80 text-sm hover:text-white transition-colors">
                  Fredrik@petrobell.se
                </a>
                <a href="mailto:Christoffer@petrobell.se" className="block text-white/80 text-sm hover:text-white transition-colors">
                  Christoffer@petrobell.se
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
